import { FormEvent } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { DataTable, DataRow } from "@/components/ui/data-table"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

type UserDetail = {
  id: number
  name: string
  email: string
  admin: boolean
  status: "active" | "pending" | "deactivated"
  timezone: string | null
  invited_at: string | null
  invite_accepted_at: string | null
  deactivated_at: string | null
  created_at: string
  updated_at: string
}

export default function AdminUserShow({ user }: { user: UserDetail }) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isSelf = props.current_user?.id === user.id

  const form = useForm({ name: user.name, email: user.email, admin: user.admin })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`/admin/users/${user.id}`, { preserveScroll: true })
  }

  return (
    <>
      <Head title={user.name}>
        <meta name="description" content={`Manage the account for ${user.name}.`} />
        <meta property="og:title" content={user.name} />
        <meta property="og:description" content={`Manage the account for ${user.name}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{user.name}</h1>
              <p className="mt-1">{user.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {user.admin && <Badge tone="accent">Admin</Badge>}
              {user.status === "pending" && <Badge tone="signal">Invite pending</Badge>}
              {user.status === "deactivated" && <Badge tone="muted">Deactivated</Badge>}
              <Link
                href="/admin/users"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to users
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-8 max-w-md">
          <h2>Details</h2>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="name">Name</label>
              <Input
                id="name"
                type="text"
                required
                aria-invalid={!!errors.name}
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
              />
              {errors.name && (
                <p className="text-xs text-danger-display">{errors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                required
                aria-invalid={!!errors.email}
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm font-normal text-ink-body">
              <Checkbox
                checked={form.data.admin}
                disabled={isSelf}
                onChange={(e) => form.setData("admin", e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-ink-display">Administrator</span>
                <span className="block text-xs text-ink-muted">
                  {isSelf
                    ? "You can't change your own admin role."
                    : "Can invite and manage users."}
                </span>
              </span>
            </label>
            <Button type="submit" disabled={form.processing}>
              Save changes
            </Button>
          </form>
        </section>

        {(user.status === "pending" || !isSelf) && (
          <section className="mt-10 max-w-md">
            <h2>Account</h2>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {user.status === "pending" && (
                <Button
                  variant="secondary"
                  onClick={() => router.post(`/admin/users/${user.id}/resend_invite`)}
                >
                  Resend invite
                </Button>
              )}
              {!isSelf && user.status !== "deactivated" && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost">Deactivate</Button>
                  </DialogTrigger>
                  <DialogContent size="sm">
                    <DialogHeader>
                      <DialogTitle>Deactivate {user.name}?</DialogTitle>
                      <DialogDescription>
                        They will be signed out everywhere and won&apos;t be able
                        to log in until reactivated. Their work and history stay
                        intact.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="ghost">Cancel</Button>
                      </DialogClose>
                      <Button
                        variant="danger"
                        onClick={() => router.patch(`/admin/users/${user.id}/deactivate`)}
                      >
                        Deactivate account
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {!isSelf && user.status === "deactivated" && (
                <Button
                  variant="secondary"
                  onClick={() => router.patch(`/admin/users/${user.id}/reactivate`)}
                >
                  Reactivate
                </Button>
              )}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2>Metadata</h2>
          <div className="mt-4">
            <DataTable>
              <DataRow title="Timezone">{user.timezone ?? "—"}</DataRow>
              <DataRow title="Joined">{formatDateTime(user.created_at)}</DataRow>
              <DataRow title="Invited">
                {user.invited_at ? formatDateTime(user.invited_at) : "—"}
              </DataRow>
              <DataRow title="Invite accepted">
                {user.invite_accepted_at ? formatDateTime(user.invite_accepted_at) : "—"}
              </DataRow>
              <DataRow title="Deactivated">
                {user.deactivated_at ? formatDateTime(user.deactivated_at) : "—"}
              </DataRow>
              <DataRow title="Updated">{formatDateTime(user.updated_at)}</DataRow>
            </DataTable>
          </div>
        </section>
      </AdminShell>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
